import { FormApi } from "final-form";
import React, { useEffect, useRef } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectCurrentWorkspaceId } from "src/store/app";
import { makeStyles } from "tss-react/mui";

import { Theme } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { NumberField } from "@shared/form/NumberField";
import { TextField } from "@shared/form/TextField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { CreateClientPayload } from "../../api";
import { resellerDescriptionMaxLength } from "../../types";

type FormValues = {
    organization: string;
    expectedDevices: string;
    description: string;
};

const useStyles = makeStyles()((theme: Theme) => ({
    form: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
}));

type Props = {
    open: boolean;
    onClose: () => void;
    onClientCreated?: (payload: CreateClientPayload) => Promise<unknown>;
};

const AddClientDialog = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);
    const formRef = useRef<FormApi<FormValues> | null>(null);

    useEffect(() => {
        if (props.open && formRef.current) {
            formRef.current.restart();
        }
    }, [props.open]);

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.organization) {
            errors.organization = t("modals:addClient.input.organizationName.validation.required");
        }

        if (!values.expectedDevices) {
            errors.expectedDevices = t("modals:addClient.input.expectedDevices.validation.required");
        } else if (!/^\d+$/.test(values.expectedDevices)) {
            errors.expectedDevices = t("modals:addClient.input.expectedDevices.validation.invalidNumber");
        }

        if (values.description && values.description.length > resellerDescriptionMaxLength) {
            errors.description = t("common:validation.maxLength", { max: resellerDescriptionMaxLength });
        }

        return errors;
    };

    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const onSubmit = async (values: FormValues) => {
        if (!workspaceId) {
            return;
        }

        try {
            await props.onClientCreated?.({
                workspaceId,
                organizationName: values.organization,
                licenseLimit: parseInt(values.expectedDevices, 10),
                resellerDescription: values.description?.trim() || undefined,
            });
            showSuccessToast(t("modals:addClient.toast.clientAddedSuccess"));
            props.onClose();
        } catch (error) {
            const errorMessage = (error as Error)?.message || t("modals:addClient.toast.clientCreationFailed");
            showErrorToast(errorMessage);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape") {
            props.onClose();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            initialValues={{ organization: "", expectedDevices: "", description: "" }}
            render={({ handleSubmit, submitting, form }) => {
                formRef.current = form;
                return (
                    <Modal
                        title={t("modals:addClient.title")}
                        onKeyDown={onKeyDown}
                        open={props.open}
                        onClose={props.onClose}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        }
                    >
                        <form onSubmit={handleSubmit} className={classes.form}>
                            <TextField
                                label={t("common:label.organizationName")}
                                fullWidth
                                name="organization"
                                placeholder={t("modals:addClient.input.organizationName.placeholder")}
                                showErrorOnSubmit={true}
                            />
                            <NumberField
                                label={t("modals:addClient.input.expectedDevices.caption")}
                                fullWidth
                                name="expectedDevices"
                                placeholder={"50"}
                                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                showErrorOnSubmit={true}
                            />
                            <TextField
                                label={t("common:label.description")}
                                fullWidth
                                name="description"
                                placeholder={t("modals:addClient.input.description.placeholder")}
                                multiline
                                minRows={3}
                            />
                        </form>
                    </Modal>
                );
            }}
        />
    );
};

export default AddClientDialog;
